import { atom } from 'recoil'
import type { UserProfileData, Branch, ExchangeRate, ExchangeTransaction } from '../api/types'
import type { ReceiptTransactionData } from '@abcpay/shared'
import { syncTokenEffect, syncBaseUrlEffect } from './effects'

// ============================================
// Build Environment (fixed at build time)
// ============================================
const API_URLS = {
  development: 'https://api-dev.abcpay.co.kr',
  production: 'https://api.abcpay.co.kr',
} as const

const BUILD_MODE = import.meta.env.MODE

export const BUILD_ENVIRONMENT: 'development' | 'production' =
  BUILD_MODE === 'development' ? 'development' : 'production'

export const BUILD_API_BASE_URL =
  import.meta.env.VITE_API_BASE_URL || API_URLS[BUILD_ENVIRONMENT]

// ============================================
// Auth Atoms
// ============================================

export const userAtom = atom<UserProfileData | null>({
  key: 'user',
  default: null,
})

export const authTokenAtom = atom<string | null>({
  key: 'authToken',
  default: null,
  effects: [syncTokenEffect],
})

export const branchesAtom = atom<Branch[]>({
  key: 'branches',
  default: [],
})

export const selectedBranchAtom = atom<Branch | null>({
  key: 'selectedBranch',
  default: null,
})

export const environmentAtom = atom<'development' | 'production'>({
  key: 'environment',
  default: BUILD_ENVIRONMENT,
})

export const apiBaseUrlAtom = atom<string>({
  key: 'apiBaseUrl',
  default: BUILD_API_BASE_URL,
  effects: [syncBaseUrlEffect],
})

export const isAuthLoadingAtom = atom<boolean>({
  key: 'isAuthLoading',
  default: true,
})

// ============================================
// Exchange Rate Atoms
// ============================================

export const exchangeRatesAtom = atom<Map<string, ExchangeRate>>({
  key: 'exchangeRates',
  default: new Map(),
})

export const isLoadingRatesAtom = atom<boolean>({
  key: 'isLoadingRates',
  default: false,
})

export const ratesErrorAtom = atom<string | null>({
  key: 'ratesError',
  default: null,
})

export const ratesLastUpdatedAtom = atom<Date | null>({
  key: 'ratesLastUpdated',
  default: null,
})

// ============================================
// Transaction Atoms (for receipt reprinting)
// ============================================

export const lastTransactionAtom = atom<{
  transactionData: ReceiptTransactionData | null
  exchangeResponse: ExchangeTransaction | null
}>({
  key: 'lastTransaction',
  default: {
    transactionData: null,
    exchangeResponse: null
  }
})
